import { useQuery, useQueryClient } from '@tanstack/react-query'
import { DateTime } from 'luxon'
import { useCallback, useMemo, useState } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { currentUserPersonQuery, dashboardGet, personalDashboardGet } from '../../api'
import { Button } from '../../common/Button.tsx'
import {
	HeadingName,
	PersonalDashboardHeaderContainer,
	PersonalDashboardHeaderInfo,
	PersonalImageContainer,
	TodayDate,
	WelcomeImage,
	WelcomeImageContainer,
} from '../../common/dashboardheader/dashboardheader.styled.jsx'
import { IconCamera } from '../../common/icons'
import { LoadingButton } from '../../common/loaders/loadingbutton'
import { eSalaryImage, localized } from '../../common/utils'
import { DashboardCards } from '../../dashboardview/dashboardcards'
import { authSelector } from '../../reducers'
import PersonModal from '../personnel-view/modals/person/personmodal.jsx'
import ProfileImageModal from './profileimage'

export default function PersonalDashboardView() {
	const { fullName, eSalaryCompanyId, employeeNumber } = useSelector(authSelector)
	const { t } = useTranslation()
	const localDateTime = DateTime.local().setLocale(localized.resolvedLanguage) // localWeekNumber

	const [profileImageModalShow, setProfileImageModalShow] = useState(false)
	const [modalShow, setModalShow] = useState(false)
	const [loading, setLoading] = useState(false)

	const joinNames = useCallback(
		(names) => {
			if (!names || names.length === 0) return null
			if (names.length === 1) return names[0]

			const allButLast = names.slice(0, -1).join(', ')
			const last = names[names.length - 1]
			return `${allButLast} ${t('and')} ${last}`
		},
		[t],
	)

	const queryClient = useQueryClient()

	const dashboardQuery = useQuery({
		queryKey: ['dashboard'],
		queryFn: () => dashboardGet().then((res) => res.data),
	})
	const namedays = dashboardQuery.data ?? null
	const namedaysMemo = useMemo(() => joinNames(namedays), [namedays, joinNames])

	const personalDashboardQuery = useQuery({
		queryKey: ['personalDashboard'],
		queryFn: () => personalDashboardGet().then((res) => res.data),
	})
	const dashboardCards = useMemo(() => {
		if (!personalDashboardQuery.data) return null
		return <DashboardCards personalDashboardData={personalDashboardQuery.data} />
	}, [personalDashboardQuery.data])

	const personDataQuery = useQuery(currentUserPersonQuery(employeeNumber))
	const personData = personDataQuery.data
	const refreshPersonData = useCallback(
		() => queryClient.invalidateQueries(currentUserPersonQuery(employeeNumber)),
		[employeeNumber, queryClient.invalidateQueries],
	)

	const personImage = personData?.otherDetails?.image
	const profileImageModal = useMemo(
		() =>
			profileImageModalShow && (
				<ProfileImageModal // do not re-render ProfileImageModal on every tick
					show={profileImageModalShow}
					setShow={setProfileImageModalShow}
					hasImage={!!personImage}
					personData={personData}
					refreshPersonData={refreshPersonData}
				/>
			),
		[profileImageModalShow, personImage, refreshPersonData, personData],
	)

	return (
		personData && (
			<>
				{modalShow && <PersonModal show setShow={setModalShow} personData={personData} />}
				{profileImageModal}
				<PersonalDashboardHeaderContainer>
					<PersonalImageContainer className="personal-profile-wrapper">
						{personImage ? (
							<img
								src={eSalaryImage(eSalaryCompanyId, personImage)}
								crossOrigin="anonymous"
								className="personal-profile-image"
								alt={fullName}
							/>
						) : (
							<div className="personal-avatar-fallback">
								{fullName
									?.split(' ')
									.map((n) => n[0])
									.join('')
									.toUpperCase()}
							</div>
						)}
						<OverlayTrigger placement="top" overlay={<Tooltip>{t('EditProfileImage', 'Vaihda profiilikuva')}</Tooltip>}>
							{/** biome-ignore lint/a11y/noStaticElementInteractions: TODO */}
							{/** biome-ignore lint/a11y/useKeyWithClickEvents: TODO */}
							<div className="personal-camera-overlay" onClick={() => setProfileImageModalShow(true)}>
								<div className="personal-camera-icon">
									<IconCamera className="personal-camera-svg" />
								</div>
							</div>
						</OverlayTrigger>
					</PersonalImageContainer>
					<PersonalDashboardHeaderInfo>
						<div>
							<HeadingName>
								{t('GreetingWorkday', 'Mukavaa työpäivää')} {personData?.forename}!
							</HeadingName>
							<Button
								testId="open-profile-modal"
								variant="outline-primary"
								onClick={async () => {
									setLoading(true)
									await personDataQuery.refetch()
									setLoading(false)
									setModalShow(true)
								}}
							>
								{t('MyProfile', 'Oma profiili')} &nbsp;
								{loading && <LoadingButton />}
							</Button>
						</div>
						<div style={{ backgroundColor: '#FFFFFFCC', borderRadius: '8px', padding: '10px', gap: '5px' }}>
							<HeadingName className="d-none d-xxl-block"></HeadingName>
							<div>
								{t('TodayIs', 'Tänään on')}
								<TodayDate>
									{localDateTime.toLocaleString()}
									{', '}
									{t('week', 'viikko')} {localDateTime.weekNumber}
								</TodayDate>
							</div>
						</div>
						{namedaysMemo && (
							<div style={{ backgroundColor: '#FFFFFFCC', borderRadius: '8px', padding: '10px', gap: '5px' }}>
								<HeadingName className="d-none d-xxl-block"></HeadingName>
								<div>
									{t('NamedaysToday', 'Nimipäivää viettää tänään')}
									<TodayDate>{namedaysMemo}</TodayDate>
								</div>
							</div>
						)}
					</PersonalDashboardHeaderInfo>
					<WelcomeImageContainer>
						<WelcomeImage />
					</WelcomeImageContainer>
				</PersonalDashboardHeaderContainer>

				{dashboardCards}
			</>
		)
	)
}
